/**
 * WCAG 2.4.11 — Focus Not Obscured (Minimum)
 *
 * Shopify Privacy Center cookie banner (.shopify-pc__banner__dialog) is
 * injected at bottom-left and can fully cover a focused element.
 * This script:
 *   1. Reads the banner's rendered height into --cookie-banner-height
 *      so CSS scroll-padding-bottom matches it.
 *   2. On focusin, if the focused element overlaps the banner rect,
 *      add [data-focus-overlap-cookie] to <html> so CSS fades the banner.
 */
(() => {
  const html = document.documentElement;
  let banner = null;
  let rafId = 0;

  const findBanner = () =>
    document.querySelector('.shopify-pc__banner__dialog');

  const updateBannerHeight = () => {
    if (!banner) return;
    const h = banner.getBoundingClientRect().height;
    if (h > 0) html.style.setProperty('--cookie-banner-height', `${Math.ceil(h + 24)}px`);
  };

  const overlapsBanner = (el) => {
    if (!banner || !el || typeof el.getBoundingClientRect !== 'function') return false;
    if (banner.contains(el)) return false;
    const a = el.getBoundingClientRect();
    const b = banner.getBoundingClientRect();
    if (b.width === 0 || b.height === 0) return false;
    return a.bottom > b.top && a.top < b.bottom && a.right > b.left && a.left < b.right;
  };

  const onFocusIn = (event) => {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      if (!banner) banner = findBanner();
      if (overlapsBanner(event.target)) {
        html.setAttribute('data-focus-overlap-cookie', '');
      } else {
        html.removeAttribute('data-focus-overlap-cookie');
      }
    });
  };

  const onFocusOut = () => {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      if (!document.activeElement || document.activeElement === document.body) {
        html.removeAttribute('data-focus-overlap-cookie');
      }
    });
  };

  const init = () => {
    banner = findBanner();
    updateBannerHeight();

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('resize', updateBannerHeight);

    // Banner may appear / change / disappear after load (Shopify injects async).
    // Watch only direct children of <body> — banner is injected as a top-level
    // node. Avoid subtree observation so we do not contend with lazysizes,
    // Vimeo/YouTube player DOM mutations, or other in-page widgets.
    const obs = new MutationObserver(() => {
      const next = findBanner();
      if (next !== banner) {
        banner = next;
        updateBannerHeight();
      }
    });
    obs.observe(document.body, { childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
